import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Star, Heart, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChildrenCoaching = () => {
  const [formData, setFormData] = useState({
    parent_name: '', parent_email: '', parent_phone: '',
    child_name: '', child_age: '', age_group: 'juniors-5-12',
    location: '', about_child: '', goals: '', additional_info: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'child_age') {
        const age = parseInt(value);
        if (age >= 5 && age <= 12) updated.age_group = 'juniors-5-12';
        else if (age >= 13 && age <= 17) updated.age_group = 'teens-13-17';
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...formData, child_age: parseInt(formData.child_age) };
      const resp = await fetch(`${API_URL}/api/applications/children`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.detail || data.message || 'Something went wrong');
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  const inputStyle = "w-full bg-page border border-border-medium text-primary p-3 focus:border-primary focus:outline-none";
  const labelStyle = "block text-sm font-medium text-secondary mb-2";

  return (
    <div className="min-h-screen pt-32" data-testid="children-coaching-page">
      {/* Hero */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-sm tracking-widest text-secondary mb-4 uppercase">Young Performers Programme</p>
            <h1 className="heading-1 mb-8">CHILDREN&apos;S COACHING</h1>
            <p className="body-large max-w-3xl mb-6">
              GPS Method™ adapted for young performers aged 5-17.
              Building confidence, presence, and self-expression from the ground up.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#programmes" className="btn-primary">
                View Programmes <ArrowRight className="ml-2" size={18} />
              </a>
              <a href="#apply" className="btn-secondary">
                Apply Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Children's Coaching */}
      <section className="py-20 bg-card">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 mb-8">WHY START YOUNG?</h2>
            <p className="body-medium mb-6">
              The foundations of presence, self-awareness, and emotional grounding are most powerful when developed early. Children who learn these skills carry them for life — not just on stage, but in every room they enter.
            </p>
            <p className="body-medium text-secondary">
              The GPS Method™ for young performers is not about creating child actors. It&apos;s about nurturing confident, grounded, expressive young people who understand themselves from the inside out.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="heading-2 mb-16 text-center">OUR APPROACH</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card-standard p-8 text-center">
              <Shield size={32} className="text-primary mx-auto mb-4" />
              <h3 className="heading-5 mb-3 text-primary">Safe Space</h3>
              <p className="body-small text-secondary">
                A nurturing, judgement-free environment where every child feels seen and supported.
              </p>
            </div>
            <div className="card-standard p-8 text-center">
              <Users size={32} className="text-primary mx-auto mb-4" />
              <h3 className="heading-5 mb-3 text-primary">Small Groups</h3>
              <p className="body-small text-secondary">
                Maximum 8 children per group, ensuring individual attention and genuine connection.
              </p>
            </div>
            <div className="card-standard p-8 text-center">
              <Star size={32} className="text-primary mx-auto mb-4" />
              <h3 className="heading-5 mb-3 text-primary">Age-Appropriate</h3>
              <p className="body-small text-secondary">
                Programmes designed specifically for each developmental stage, never rushed.
              </p>
            </div>
            <div className="card-standard p-8 text-center">
              <Heart size={32} className="text-primary mx-auto mb-4" />
              <h3 className="heading-5 mb-3 text-primary">Parent Involved</h3>
              <p className="body-small text-secondary">
                Regular updates, open communication, and parent observation sessions included.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programmes */}
      <section className="py-20 bg-card" id="programmes">
        <div className="container-custom">
          <h2 className="heading-2 mb-16 text-center">PROGRAMMES</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Juniors */}
            <div className="card-standard p-8 border border-border-medium">
              <div className="text-sm tracking-widest text-secondary mb-2 uppercase">Ages 5-12</div>
              <h3 className="heading-3 mb-4 text-primary">JUNIORS</h3>
              <p className="body-small text-secondary mb-6">
                Playful, exploratory sessions that build confidence, body awareness, and creative expression through games, movement, and storytelling.
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">Grounding through play and movement</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">Confidence and self-expression exercises</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">Creative storytelling and imagination work</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">Emotional awareness in a safe setting</span>
                </div>
              </div>
              <div className="text-sm text-secondary">
                <p><strong className="text-primary">Duration:</strong> 10-week term</p>
                <p><strong className="text-primary">Sessions:</strong> Weekly, 60 minutes</p>
                <p><strong className="text-primary">Group size:</strong> Max 8 children</p>
              </div>
            </div>

            {/* Teens */}
            <div className="card-standard p-8 border border-primary">
              <div className="text-sm tracking-widest text-secondary mb-2 uppercase">Ages 13-17</div>
              <h3 className="heading-3 mb-4 text-primary">TEENS</h3>
              <p className="body-small text-secondary mb-6">
                Focused, structured sessions that develop psychological presence, performance skills, and internal strength for young actors and performers.
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">GPS Method™ fundamentals adapted for teens</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">Audition preparation and stage presence</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">Emotional regulation and performance focus</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="body-small text-secondary">Building a personal creative process</span>
                </div>
              </div>
              <div className="text-sm text-secondary">
                <p><strong className="text-primary">Duration:</strong> 12-week term</p>
                <p><strong className="text-primary">Sessions:</strong> Weekly, 90 minutes</p>
                <p><strong className="text-primary">Group size:</strong> Max 8 teens</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20" id="apply">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <h2 className="heading-2 mb-4 text-center">APPLY FOR YOUR CHILD</h2>
            <p className="body-medium text-secondary text-center mb-12">
              Tell us about your child and what you're hoping they'll gain from the programme.
            </p>

            {submitted ? (
              <div className="bg-card border border-primary p-8 text-center" data-testid="children-form-success">
                <CheckCircle size={48} className="text-primary mx-auto mb-4" />
                <h3 className="heading-4 mb-4 text-primary">Application Received</h3>
                <p className="body-medium text-secondary mb-6">
                  Thank you for your interest in GPS Method™ Children&apos;s Coaching. We&apos;ll review your application and contact you within 5 business days.
                </p>
                <Link to="/" className="btn-secondary inline-flex items-center">
                  Back to Home
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="children-coaching-form">
                {/* Parent/Guardian Info */}
                <div className="bg-card p-6 space-y-4">
                  <h3 className="heading-5 text-primary mb-2">Parent / Guardian</h3>
                  <div>
                    <label className={labelStyle}>Full Name *</label>
                    <input type="text" name="parent_name" value={formData.parent_name} onChange={handleChange} required className={inputStyle} data-testid="input-parent-name" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelStyle}>Email *</label>
                      <input type="email" name="parent_email" value={formData.parent_email} onChange={handleChange} required className={inputStyle} data-testid="input-parent-email" />
                    </div>
                    <div>
                      <label className={labelStyle}>Phone *</label>
                      <input type="tel" name="parent_phone" value={formData.parent_phone} onChange={handleChange} required className={inputStyle} data-testid="input-parent-phone" />
                    </div>
                  </div>
                </div>

                {/* Child Info */}
                <div className="bg-card p-6 space-y-4">
                  <h3 className="heading-5 text-primary mb-2">Child Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelStyle}>Child's Name *</label>
                      <input type="text" name="child_name" value={formData.child_name} onChange={handleChange} required className={inputStyle} data-testid="input-child-name" />
                    </div>
                    <div>
                      <label className={labelStyle}>Age *</label>
                      <input type="number" name="child_age" min="5" max="17" value={formData.child_age} onChange={handleChange} required className={inputStyle} data-testid="input-child-age" />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Programme</label>
                    <select name="age_group" value={formData.age_group} onChange={handleChange} className={inputStyle} data-testid="select-age-group">
                      <option value="juniors-5-12">Juniors (Ages 5-12)</option>
                      <option value="teens-13-17">Teens (Ages 13-17)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelStyle}>Location *</label>
                    <input type="text" name="location" value={formData.location} onChange={handleChange} required placeholder="City, Country" className={inputStyle} data-testid="input-location" />
                  </div>
                </div>

                {/* About & Goals */}
                <div className="bg-card p-6 space-y-4">
                  <h3 className="heading-5 text-primary mb-2">Tell Us More</h3>
                  <div>
                    <label className={labelStyle}>About Your Child *</label>
                    <textarea name="about_child" value={formData.about_child} onChange={handleChange} required rows={4} placeholder="Tell us about your child&apos;s interests, personality, and any experience in performance or creative arts..." className={inputStyle} data-testid="input-about-child" />
                  </div>
                  <div>
                    <label className={labelStyle}>What Are Your Goals? *</label>
                    <textarea name="goals" value={formData.goals} onChange={handleChange} required rows={4} placeholder="What would you like your child to gain from this programme?" className={inputStyle} data-testid="input-goals" />
                  </div>
                  <div>
                    <label className={labelStyle}>Anything Else We Should Know?</label>
                    <textarea name="additional_info" value={formData.additional_info} onChange={handleChange} rows={3} placeholder="Any additional information, special requirements, or questions..." className={inputStyle} data-testid="input-additional-info" />
                  </div>
                </div>

                {error && <div className="text-red-400 text-sm" data-testid="form-error">{error}</div>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full justify-center"
                  data-testid="submit-children-form"
                  style={{ opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChildrenCoaching;
